import React, { ReactNode, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store/configureStore';
import { gapsOp } from 'store/ducks/Gaps';
import { ISelectionOption } from 'store/ducks/globalTypes';
import * as Styled from './DragTextBox.styled';

interface ITextBoxProps {
  children: string | ReactNode;
  onIdChange?: (
    newId: number | null,
    dragText: string | null,
    selectedValue: string | null
  ) => void;
}

interface ClnReqItem {
  category: string;
  id: number;
  is_app: string;
  level: { level: number; font: string; size: number };
  tech_child: string;
  value: null;
}

const DragTextBox: React.FC<ITextBoxProps> = ({ children, onIdChange }) => {
  const dispatch = useDispatch();
  const [selectedValue, setSelectedValue] = useState<string>('Search for id');
  const [selectedChild, setSelectedChild] = useState<string | null>();
  const [options, setOptions] = useState<ISelectionOption[]>([]);
  const [assignedEl, setAssignedEl] = useState({
    afg_id: 0,
    bru_id: ''
  });
  const [assignedEls, setAssignedEls] = useState<
    { afg_id: number; bru_id: string }[]
  >([]);
  const [hasDispatched, setHasDispatched] = useState(false);

  const { auditFindingMapping, clickedHandleMoveButton, scheme } = useSelector(
    (state: RootState) => ({
      auditFindingMapping: state.gaps.auditFindingMapping,
      clickedHandleMoveButton: state.gaps.clickedHandleMoveButton,
      scheme: state.global.scheme
    })
  );

  const handleSelectChange = (value: unknown) => {
    const stringValue = String(value);
    setSelectedValue(stringValue);
    setSelectedChild(children as string);
    if (onIdChange) {
      onIdChange(
        parseInt(stringValue) || null,
        children as string,
        selectedValue || null
      );
    }
  };

  useEffect(() => {
    // Only update if a valid parent ID is selected (not the default text)
    if (selectedValue && selectedValue !== 'Search for id') {
      const parentId = parseInt(selectedValue);
      if (!isNaN(parentId)) {
        setAssignedEl((currentState) => ({
          ...currentState,
          bru_id: parentId.toString(), // Selected parent ID from dropdown
          afg_id: 0 // Will be set from audit finding text
        }));
      }
    }
  }, [selectedValue, selectedChild]);

  useEffect(() => {
    setAssignedEls((currentEls) => [...currentEls, assignedEl]);
  }, [assignedEl]);

  useEffect(() => {
    if (clickedHandleMoveButton) {
      setSelectedValue('Search for id');
      setHasDispatched(false);
      dispatch(gapsOp.setClickedHandleMoveButton(false));
    }
  }, [clickedHandleMoveButton]);

  useEffect(() => {
    // Only dispatch once when this component's dropdown value changes (not default)
    if (
      assignedEl.bru_id &&
      selectedValue !== 'Search for id' &&
      selectedChild === children &&
      !hasDispatched
    ) {
      dispatch(
        gapsOp.setAssignedEls({
          id: selectedChild || '', // Store audit finding text for matching
          txt: assignedEl.bru_id // Selected parent ID from dropdown
        })
      );
      setHasDispatched(true);
    }
  }, [
    assignedEl.bru_id,
    selectedValue,
    selectedChild,
    dispatch,
    children,
    hasDispatched
  ]);

  useEffect(() => {
    if (auditFindingMapping && auditFindingMapping.length > 0) {
      const parentItemIds: string[] = [];

      // The auditFindingMapping is already the array of items, not nested
      auditFindingMapping.forEach((item: { id?: number }) => {
        if (item && item.id) {
          parentItemIds.push(item.id.toString());
        }
      });

      // Remove duplicates and create options with CLN Req names as labels
      const uniqueIds = [...new Set(parentItemIds)];
      const newOptions = uniqueIds.map((id: string) => {
        // Find the corresponding item to get its value (CLN Req name)
        const matchingItem = auditFindingMapping.find(
          (item: { id?: number; value?: string }) => item.id?.toString() === id
        );
        return {
          value: id,
          label: matchingItem?.value || id // Use CLN Req name as label, fallback to ID
        };
      });

      setOptions(newOptions);
    } else {
      setOptions([]);
    }
  }, [auditFindingMapping]);

  return (
    <Styled.StyledMainDiv className='drag-text-box'>
      <div>
        <Styled.StyledSelect
          className='select-search'
          defaultActiveFirstOption
          showSearch
          optionFilterProp='children'
          virtual={false}
          filterOption={(input, option) => {
            const label = option?.label ?? '';
            if (typeof label !== 'string') {
              return false;
            }
            return (
              label.toLowerCase().indexOf(input.toLowerCase()) >= 0 ||
              label.toLowerCase().indexOf(' ' + input.toLowerCase()) >= 0 ||
              label.toLowerCase().indexOf(input.toLowerCase() + ' ') >= 0
            );
          }}
          filterSort={(optionA, optionB) =>
            (optionA?.label ?? '')
              .toLowerCase()
              .localeCompare((optionB?.label ?? '').toLowerCase())
          }
          options={options}
          onChange={handleSelectChange}
          value={selectedValue}
        />
      </div>
      <Styled.StyledSpan scheme={scheme}>{children}</Styled.StyledSpan>
    </Styled.StyledMainDiv>
  );
};

export default DragTextBox;
